
import React, { useState } from 'react';
import { SiteSettings, Subscriber, CustomPage } from '../types';
import { Bot, Mail, Instagram, Youtube, ExternalLink, ChevronRight, User as UserIcon, CheckCircle, ArrowRight, Facebook, Linkedin, Phone, Database, CloudCheck, Layout } from 'lucide-react';
import { getBasePath } from '../constants';

// Ícone Oficial do WhatsApp
const WhatsappLogo = ({ size = 20 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 448 512" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M380.9 97.1C339 55.1 283.2 32 223.9 32c-122.4 0-222 99.6-222 222 0 39.1 10.2 77.3 29.6 111L0 480l117.7-30.9c32.4 17.7 68.9 27 106.1 27h.1c122.3 0 224.1-99.6 224.1-222 0-59.3-25.2-115-67.1-157zm-157 341.6c-33.2 0-65.7-8.9-94-25.7l-6.7-4-69.8 18.3L72 359.2l-4.4-7c-18.5-29.4-28.2-63.3-28.2-98.2 0-101.7 82.8-184.5 184.6-184.5 49.3 0 95.6 19.2 130.4 54.1 34.8 34.9 56.2 81.2 56.1 130.5 0 101.8-84.9 184.6-186.6 184.6zm101.2-138.2c-5.5-2.8-32.8-16.2-37.9-18-5.1-1.9-8.8-2.8-12.5 2.8-3.7 5.6-14.3 18-17.6 21.8-3.2 3.7-6.5 4.2-12 1.4-5.5-2.8-23.4-8.6-44.6-27.5-16.4-14.7-27.5-32.8-30.7-38.3-3.2-5.6-.3-8.6 2.5-11.4 2.5-2.5 5.5-6.5 8.3-9.7 2.8-3.3 3.7-5.5 5.5-9.3 1.9-3.7.9-6.9-.5-9.7-1.4-2.8-12.5-30.1-17.1-41.2-4.5-10.8-9.1-9.3-12.5-9.5-3.2-.2-6.9-.2-10.6-.2-3.7 0-9.7 1.4-14.8 6.9-5.1 5.6-19.4 19-19.4 46.3 0 27.3 19.9 53.7 22.6 57.4 2.8 3.7 39.1 59.7 94.8 83.8 13.2 5.8 23.5 9.2 31.5 11.8 13.3 4.2 25.4 3.6 35 2.2 10.7-1.6 32.8-13.4 37.4-26.4 4.6-13 4.6-24.1 3.2-26.4-1.3-2.5-5-3.9-10.5-6.6z"/>
  </svg>
);

const XIcon = ({ size = 18, className = "" }: { size?: number, className?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className} xmlns="http://www.w3.org/2000/svg">
    <path d="M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932ZM17.61 20.644h2.039L6.486 3.24H4.298Z" />
  </svg>
);

const TikTokIcon = ({ size = 18, className = "" }: { size?: number, className?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className} xmlns="http://www.w3.org/2000/svg">
    <path d="M19.589 6.686a4.793 4.793 0 0 1-3.77-4.245V2h-3.445v13.672a2.896 2.896 0 0 1-5.201 1.743l-.002-.001.002.001a2.895 2.895 0 0 1 3.183-4.51v-3.5a6.329 6.329 0 0 0-5.394 10.692 6.33 6.33 0 0 0 10.857-4.424V8.687a8.182 8.182 0 0 0 4.773 1.526V6.79a4.831 4.831 0 0 1-1.003-.104z" />
  </svg>
);

interface FooterProps {
  settings: SiteSettings;
  onNavigate: (page: string) => void;
  onSubscribe?: (data: Omit<Subscriber, 'id' | 'date'>) => Promise<any> | any;
  customPages?: CustomPage[];
}

const Footer: React.FC<FooterProps> = ({ settings, onNavigate, onSubscribe, customPages = [] }) => {
  const currentYear = new Date().getFullYear();
  
  const [form, setForm] = useState({ name: '', email: '', phone: '', answer: '' });
  const [captcha, setCaptcha] = useState({ 
    a: Math.floor(Math.random() * 10) + 1, 
    b: Math.floor(Math.random() * 10) + 1 
  });
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'loading'>('idle');

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (parseInt(form.answer) !== captcha.a + captcha.b) {
        alert('Soma incorreta. Tente novamente.');
        setCaptcha({ a: Math.floor(Math.random() * 10) + 1, b: Math.floor(Math.random() * 10) + 1 });
        setForm(prev => ({...prev, answer: ''}));
        return;
    }
    setFormStatus('loading');
    
    const result = await onSubscribe?.({ name: form.name, email: form.email, phone: form.phone });
    
    if (result !== false) {
        setFormStatus('success');
        setForm({ name: '', email: '', phone: '', answer: '' });
    } else {
        setFormStatus('idle');
        alert('Erro ao realizar cadastro. Tente novamente mais tarde.');
    }
  };

  const cleanWhatsApp = settings.whatsappNumber?.replace(/\D/g, '') || '5516992091604';
  const whatsappLink = `https://wa.me/${cleanWhatsApp}`;

  // Configurações Dinâmicas
  const bgColor = settings.footerBgColor || '#0f172a';
  const titleColor = settings.footerTitleColor || '#ffffff';
  const textColor = settings.footerTextColor || '#94a3b8';
  const logoDescription = settings.footerLogoDescription || `Desenvolvendo soluções digitais inteligentes e ecossistemas de alta performance através de IA e automação para ${settings.siteTitle || 'nossos clientes'}.`;
  const newsletterTitle = settings.footerNewsletterTitle || 'Conhecimento que gera valor.';
  const newsletterSubtitle = settings.footerNewsletterSubtitle || 'Junte-se a nossa comunidade exclusiva e receba estratégias de SEO, tecnologia e negócios.';
  const showNewsletter = settings.footerShowNewsletter !== false;
  
  const contactEmail = settings.footerContactEmail || settings.smtpConfig?.fromEmail || '';
  const copyrightText = settings.footerCopyrightText || `© ${currentYear} ${settings.siteTitle || 'Portal de Conteúdo'}. TODOS OS DIREITOS RESERVADOS.`;

  const footerPages = customPages.filter(p => p.show_in_footer);
  
  // Base footer links
  const baseFooterLinks = [
    { id: 'home', label: 'Início' },
    { id: 'quem-somos', label: 'Quem Somos' },
    { id: 'produtos', label: 'Produtos' },
    { id: 'blog', label: 'Blog' },
    { id: 'webstories', label: 'Webstories' },
    { id: 'sitemap', label: 'Mapa do Site' },
    ...(settings.gamificationEnabled ? [{ id: 'beneficios', label: 'Benefícios' }] : []),
    { id: 'contato', label: 'Contato' },
  ];

  const finalFooterLinks: Array<{ id: string, label: string, isCustom?: boolean, slug?: string }> = [];

  baseFooterLinks.forEach(baseLink => {
    finalFooterLinks.push(baseLink);
    const targetingThis = footerPages.filter(p => p.position_footer_target === baseLink.id);
    targetingThis.forEach(p => {
      finalFooterLinks.push({
        id: `p/${p.slug}`,
        label: p.title,
        isCustom: true,
        slug: p.slug
      });
    });
  });

  // Filter custom pages that remain to be listed in the columns (not nested in the Links list)
  const unmatchedFooterPages = footerPages.filter(p => {
    if (!p.position_footer_target) return true;
    return !baseFooterLinks.some(bl => bl.id === p.position_footer_target);
  });

  // Chunk pages into widgets of 5
  const chunkArray = (arr: any[], size: number) => {
    const chunks = [];
    for (let i = 0; i < arr.length; i += size) {
      chunks.push(arr.slice(i, i + size));
    }
    return chunks;
  };

  const pageWidgets = chunkArray(unmatchedFooterPages, 5);

  const handlePageClick = (slug: string) => {
    onNavigate(`p/${slug}`);
  };

  return (
    <footer className="overflow-hidden relative" style={{ backgroundColor: bgColor, color: textColor }}>
      
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[600px] bg-primary/5 rounded-full blur-[120px] pointer-events-none"></div>

      {/* 1. NEWSLETTER */}
      {showNewsletter && (
        <div className="w-full pt-20 pb-12 relative z-10 border-b border-white/5">
          <div className="max-w-7xl mx-auto px-4 md:px-6 text-center">
            <div className="max-w-6xl mx-auto">
              <span className="inline-block px-4 py-1.5 rounded-lg bg-white/5 text-primary border border-white/10 text-[11px] font-bold uppercase tracking-wider mb-6 backdrop-blur-sm">
                  Newsletter VIP
              </span>
              <h3 className="text-3xl md:text-5xl font-bold mb-6 tracking-tight leading-tight" style={{ color: titleColor }}>
                {newsletterTitle}
              </h3>
              <p className="text-base md:text-lg max-w-2xl mx-auto mb-12 font-medium leading-relaxed" style={{ color: textColor }}>
                {newsletterSubtitle}
              </p>

              {formStatus === 'success' ? (
                  <div className="animate-fade-in py-10 bg-white/5 rounded-2xl border border-white/10 backdrop-blur-md max-w-2xl mx-auto">
                      <div className="w-12 h-12 bg-emerald-500 rounded-xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-emerald-500/30">
                          <CheckCircle className="text-white" size={24} />
                      </div>
                      <h4 className="text-xl font-bold mb-2" style={{ color: titleColor }}>Bem-vindo à Lista VIP!</h4>
                      <button onClick={() => setFormStatus('idle')} className="mt-4 text-white/50 hover:text-white underline text-xs font-bold uppercase tracking-wider transition-colors">Cadastrar outro</button>
                  </div>
              ) : (
                  <form onSubmit={handleNewsletterSubmit} className="flex flex-col lg:flex-row items-stretch lg:items-center gap-3 bg-white/[0.03] p-2.5 rounded-2xl border border-white/10 backdrop-blur-md animate-fade-in shadow-2xl">
                      <div className="relative flex-[2]">
                          <UserIcon className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
                          <input 
                              type="text" placeholder="Seu Nome" value={form.name} onChange={e => setForm({...form, name: e.target.value})} required
                              className="w-full bg-black/20 border border-white/5 rounded-xl pl-12 pr-4 py-4 text-white placeholder:text-slate-600 focus:bg-black/40 focus:border-primary transition-all font-medium"
                          />
                      </div>
                      <div className="relative flex-[2]">
                          <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
                          <input 
                              type="email" placeholder="Seu E-mail" value={form.email} onChange={e => setForm({...form, email: e.target.value})} required
                              className="w-full bg-black/20 border border-white/5 rounded-xl pl-12 pr-4 py-4 text-white placeholder:text-slate-600 focus:bg-black/40 focus:border-primary transition-all font-medium"
                          />
                      </div>
                      <div className="relative flex-[2]">
                          <Phone className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
                          <input 
                              type="tel" placeholder="WhatsApp" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} required
                              className="w-full bg-black/20 border border-white/5 rounded-xl pl-12 pr-4 py-4 text-white placeholder:text-slate-600 focus:bg-black/40 focus:border-primary transition-all font-medium"
                          />
                      </div>
                      <div className="flex items-center gap-2 bg-black/30 border border-white/5 rounded-xl px-4 py-4 min-w-[110px] shrink-0">
                          <span className="text-[11px] font-bold text-slate-500">{captcha.a}+{captcha.b}=</span>
                          <input type="number" placeholder="?" value={form.answer} onChange={e => setForm({...form, answer: e.target.value})} required
                              className="w-full bg-transparent border-none text-white text-center font-bold focus:outline-none placeholder:text-slate-700"
                          />
                      </div>
                      <button 
                          type="submit" disabled={formStatus === 'loading'}
                          className="lg:w-auto w-full bg-white text-slate-900 px-8 py-4 rounded-xl font-bold text-xs uppercase tracking-wider transition-all shadow-xl hover:bg-primary hover:text-white flex items-center justify-center gap-2 group disabled:opacity-50"
                      >
                          {formStatus === 'loading' ? 'Aguarde' : 'Assinar VIP'} <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                      </button>
                  </form>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 2. FOOTER CONTENT */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 relative z-10 pt-16 pb-12">
        <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-${4 + pageWidgets.length} gap-12 text-center lg:text-left`}>
          <div className="space-y-6">
            <div className="flex items-center gap-3 cursor-pointer group justify-center lg:justify-start" onClick={() => onNavigate('home')}>
              {settings.logoUrl ? (
                <img 
                  src={settings.logoUrl} 
                  alt={settings.siteTitle || "Logo"} 
                  className="h-10 w-auto object-contain group-hover:scale-105 transition-transform" 
                />
              ) : (
                <>
                  <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center text-white shadow-lg group-hover:scale-105 transition-transform">
                    <Bot size={22} />
                  </div>
                  <span className="text-lg font-bold tracking-tight uppercase" style={{ color: titleColor }}>
                    {settings.siteTitle || 'Portal'}<span className="text-primary"> de Conteúdo</span>
                  </span>
                </>
              )}
            </div>
            <p className="text-xs leading-relaxed max-w-xs font-medium mx-auto lg:mx-0" style={{ color: textColor }}>{logoDescription}</p>
            <div className="flex justify-center lg:justify-start gap-3 flex-wrap">
              <a href={whatsappLink} target="_blank" rel="noreferrer" className="w-10 h-10 bg-[#25D366] hover:bg-[#20ba5a] rounded-lg flex items-center justify-center transition-all text-white transform hover:scale-110"><WhatsappLogo size={20} /></a>
              {settings.instagramUrl && <a href={settings.instagramUrl} target="_blank" rel="noreferrer" className="w-10 h-10 bg-white/5 border border-white/10 hover:border-primary rounded-lg flex items-center justify-center transition-all text-white transform hover:scale-110"><Instagram size={18} /></a>}
              {settings.facebookUrl && <a href={settings.facebookUrl} target="_blank" rel="noreferrer" className="w-10 h-10 bg-white/5 border border-white/10 hover:border-primary rounded-lg flex items-center justify-center transition-all text-white transform hover:scale-110"><Facebook size={18} /></a>}
              {settings.linkedInUrl && <a href={settings.linkedInUrl} target="_blank" rel="noreferrer" className="w-10 h-10 bg-white/5 border border-white/10 hover:border-primary rounded-lg flex items-center justify-center transition-all text-white transform hover:scale-110"><Linkedin size={18} /></a>}
              {settings.youtubeUrl && <a href={settings.youtubeUrl} target="_blank" rel="noreferrer" className="w-10 h-10 bg-white/5 border border-white/10 hover:border-primary rounded-lg flex items-center justify-center transition-all text-white transform hover:scale-110"><Youtube size={18} /></a>}
              {settings.tiktokUrl && <a href={settings.tiktokUrl} target="_blank" rel="noreferrer" className="w-10 h-10 bg-white/5 border border-white/10 hover:border-primary rounded-lg flex items-center justify-center transition-all text-white transform hover:scale-110"><TikTokIcon size={18} /></a>}
              {settings.xUrl && <a href={settings.xUrl} target="_blank" rel="noreferrer" className="w-10 h-10 bg-white/5 border border-white/10 hover:border-primary rounded-lg flex items-center justify-center transition-all text-white transform hover:scale-110"><XIcon size={16} /></a>}
            </div>
          </div>

          <div className="flex flex-col items-center lg:items-start">
            <h4 className="text-[11px] font-bold uppercase tracking-wider mb-6" style={{ color: titleColor, opacity: 0.5 }}>Links</h4>
            <ul className="space-y-3">
              {finalFooterLinks.map(link => (
                <li key={link.id}>
                  <button 
                    onClick={() => link.isCustom && link.slug ? onNavigate(`p/${link.slug}`) : onNavigate(link.id)} 
                    className={`text-xs font-medium transition-colors hover:text-primary transition-all text-left ${link.isCustom ? 'pl-2 border-l border-primary/40' : ''}`}
                    style={{ color: textColor }}
                  >
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {pageWidgets.map((chunk, idx) => (
            <div key={idx} className="flex flex-col items-center lg:items-start">
              <h4 className="text-[11px] font-bold uppercase tracking-wider mb-6" style={{ color: titleColor, opacity: 0.5 }}>
                {idx === 0 ? 'Páginas' : `Páginas ${idx + 1}`}
              </h4>
              <ul className="space-y-3">
                {chunk.map((page: CustomPage) => (
                  <li key={page.id}>
                    <button 
                      onClick={() => handlePageClick(page.slug)} 
                      className="text-xs font-medium transition-colors hover:text-primary text-left" 
                      style={{ color: textColor }}
                    >
                      {page.title}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          <div className="flex flex-col items-center lg:items-start">
            <h4 className="text-[11px] font-bold uppercase tracking-wider mb-6" style={{ color: titleColor, opacity: 0.5 }}>Suporte</h4>
            <ul className="space-y-3">
              <li><button onClick={() => onNavigate('privacidade')} className="text-xs font-medium transition-colors hover:text-primary" style={{ color: textColor }}>Privacidade</button></li>
              <li><button onClick={() => onNavigate('termos')} className="text-xs font-medium transition-colors hover:text-primary" style={{ color: textColor }}>Termos</button></li>
            </ul>
          </div>

          <div className="flex flex-col items-center lg:items-start">
            <h4 className="text-[11px] font-bold uppercase tracking-wider mb-6" style={{ color: titleColor, opacity: 0.5 }}>Fale Conosco</h4>
            <div className="space-y-4">
                <div className="flex items-center gap-3 justify-center lg:justify-start">
                    <div className="w-8 h-8 bg-white/5 rounded-lg flex items-center justify-center text-primary border border-white/10"><Phone size={14} /></div>
                    <span className="text-xs font-medium text-white">{settings.whatsappNumber || '(16) 99209-1604'}</span>
                </div>
                <div className="flex items-center gap-3 justify-center lg:justify-start">
                    <div className="w-8 h-8 bg-white/5 rounded-lg flex items-center justify-center text-primary border border-white/10"><Mail size={14} /></div>
                    <span className="text-xs font-medium text-white">{contactEmail}</span>
                </div>
            </div>
          </div>
        </div>

        <div className="pt-10 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-6">
          <p className="text-[10px] font-medium uppercase tracking-wider text-center md:text-left opacity-40">
            {copyrightText}
          </p>
          <div className="flex flex-col items-center md:items-end">
            <span className="text-[9px] font-medium uppercase tracking-wider opacity-30 mb-1">Tecnologia por</span>
            <span className="text-[10px] font-bold flex items-center gap-1">{settings.siteTitle || 'Nossa Plataforma'}</span>
          </div>
        </div>
      </div>
      <div className="h-[1px] w-full opacity-10" style={{ backgroundColor: settings.primaryColor || '#2563eb' }}></div>
    </footer>
  );
};

export default Footer;
